import { AuthApi, PartnerApi } from "@/apis";
import type { IAuth } from "@/models/Auth";
import { usePermission } from "@/shared/permission";
import { authMe } from "@/store/Auth";
import { message } from "@/utils/message";
import { Box, LoadingOverlay, Stack, useForm } from "@dehub/theme";
import { yupResolver } from "mantine-form-yup-resolver";
import React, { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useUnsavedGuard } from "@/shared/a11y/use-unsaved-guard";
import { TransCode } from "@/shared/a11y/trans-code";
import { InfoCard } from "../InfoCard";
import { MainInfoAddressSection } from "./mainInfo-address-section";
import { MainInfoContactSection } from "./mainInfo-contact-section";
import { MainInfoIdentitySection } from "./mainInfo-identity-section";
import {
  SECTION_FIELDS,
  SECTION_IDS,
  buildPayload,
  buildSchema,
  findIncompleteSection,
  isSectionDirty,
  lockedFields,
  overlaySections,
  toFormValues,
  type IFormData,
  type SectionId,
} from "./org-form-model";

const SECTIONS: SectionId[] = ["identity", "contact", "address"];

export function MainInfoForm() {
  const { t } = useTranslation("partner");
  const { t: tCommon } = useTranslation("common");
  const schema = React.useMemo(() => buildSchema(tCommon), [tCommon]);
  const PRT_PRO_MAIN_EDIT = usePermission("PRT_PRO_MAIN_EDIT");
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const dispatch = useDispatch();

  const partner: any = user?.partner;
  const isCitizen = partner?.type === "CITIZEN";
  const registerStatus = partner?.registerStatus;
  const regNumberLocked = registerStatus === "VERIFIED" || registerStatus === "ADMIN_VERIFIED";

  // Stable identity: only a fresh partner (after authMe) produces a new baseline.
  const baseline = React.useMemo(() => toFormValues(partner), [partner]);

  // Sections open for editing. Usually one; a save that finds another section incomplete adds that
  // section next to it (keeping what was typed) and saves them together, because PUT /prt/partner
  // takes the whole body. A new partner with empty sections is the common case.
  const [editing, setEditing] = React.useState<SectionId[]>([]);
  const isEditing = editing.length > 0;
  const [saving, setSaving] = React.useState(false);
  // A second click or Enter must not start another PUT while one is in flight.
  const savingRef = React.useRef(false);

  const form = useForm<IFormData>({ initialValues: baseline, validate: yupResolver(schema) });

  // Outside edit mode the form mirrors the stored partner. Leaving edit mode (cancel or a saved
  // section) therefore puts every field back to the partner's value and drops the errors.
  React.useEffect(() => {
    if (isEditing) return;
    form.setInitialValues(baseline);
    form.setValues(baseline);
    form.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseline, isEditing]);

  // Unsaved edits: warn on reload / tab close and on any in-app navigation (sidebar, browser back).
  useUnsavedGuard(editing.some((id) => isSectionDirty(form.values, baseline, id)));

  // Focus follows the mode: into the first editable field of a newly opened section, back to the
  // card's Edit button on leaving edit mode.
  const previousEditing = React.useRef<SectionId[]>([]);
  React.useEffect(() => {
    const previous = previousEditing.current;
    previousEditing.current = editing;
    const opened = editing.find((id) => !previous.includes(id));
    if (opened) {
      setTimeout(() => {
        const candidates = Array.from(
          document.querySelectorAll<HTMLElement>(`#${SECTION_IDS[opened]} input, #${SECTION_IDS[opened]} textarea`),
        );
        const target = candidates.find((el) => !(el as HTMLInputElement).disabled && !el.closest('[class*="locked"]'));
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
        target?.focus();
      }, 0);
    } else if (editing.length === 0 && previous.length > 0) {
      setTimeout(() => {
        document.querySelector<HTMLElement>(`#${SECTION_IDS[previous[0]]} [data-edit-button]`)?.focus();
      }, 0);
    }
  }, [editing]);

  const sectionTitle = (id: SectionId) => t(`sections.${id}.title`);

  const disabledReason = (id: SectionId): ReactNode => {
    if (!PRT_PRO_MAIN_EDIT.hasAccess) return <TransCode i18nKey="partner:noEditPermission" values={{}} />;
    if (isEditing && !editing.includes(id)) return t("editingOtherSection", { section: sectionTitle(editing[0]) });
    return null;
  };

  const fieldLabel = (key: string): string => {
    const map: Record<string, string> = {
      legalEntityType: t(isCitizen ? "legalEntityTypeCitizen" : "legalEntityType"),
      equityType: t("equityType"),
      country: t("country"),
      classification: t("partnerCategory"),
      businessSectorId: t("partnerCategoryCitizen"),
      businessName: t(isCitizen ? "bizNameCitizen" : "bizName"),
      businessNameEng: t(isCitizen ? "bizNameLatinCitizen" : "fields.latin"),
      regNumber: t("fields.regNumber"),
      web: t("fields.website"),
      email: t("fields.email1"),
      email2: t("fields.email2"),
      phone: t("fields.phone1"),
      phone2: t("fields.phone2"),
      fbLink: t("fields.facebook"),
      province: tCommon("address.province"),
      district: tCommon("address.district"),
      khoroo: tCommon("address.khoroo"),
      khoroolol: tCommon("address.street"),
      khotkhonBair: tCommon("address.building"),
      khashaaDavkhar: tCommon("address.fence"),
      khaalgaDugaar: tCommon("address.door"),
      businessAddress: t("fields.addressDescription"),
      locationLat: t("fields.lat"),
      locationLng: t("fields.lng"),
    };
    return map[key] ?? key;
  };

  const reportInvalid = (key: string, error: React.ReactNode) => {
    message.error(`${fieldLabel(key)}: ${String(error ?? tCommon("validation.fillRequired"))}`);
    const node =
      form.getInputNode?.(key as keyof IFormData) ??
      document.querySelector<HTMLElement>(`[data-path="${key}"]`) ??
      document.querySelector<HTMLElement>(`[name="${key}"]`) ??
      document.querySelector<HTMLElement>(`[data-form-field="${key}"]`);
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    node?.focus?.();
  };

  const validateSections = (sections: SectionId[]): { key: string; error: React.ReactNode } | null => {
    // A locked field is never an input, so it is not checked here.
    const locked = lockedFields(regNumberLocked);
    let firstInvalid: { key: string; error: React.ReactNode } | null = null;
    for (const section of sections) {
      for (const key of SECTION_FIELDS[section]) {
        if (locked.includes(key)) continue;
        const result = form.validateField(key);
        if (result.hasError && !firstInvalid) firstInvalid = { key, error: result.error };
      }
    }
    return firstInvalid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isEditing || savingRef.current) return;

    const firstInvalid = validateSections(editing);
    if (firstInvalid) {
      reportInvalid(firstInvalid.key, firstInvalid.error);
      return;
    }

    // PUT /prt/partner takes the whole body. When another section still lacks required fields, open it
    // next to the ones being edited instead of failing: what was typed stays, and the next save sends
    // all of them together.
    const merged = overlaySections(baseline, form.values, editing);
    const gap = findIncompleteSection(schema, merged, editing);
    if (gap) {
      setEditing((current) => [...current, gap]);
      message.error(t("incompleteSection", { section: sectionTitle(gap) }));
      setTimeout(() => validateSections([gap]), 0);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      await PartnerApi.updateInfo(buildPayload(merged));
    } catch (error: any) {
      // not saved: stay in edit mode
      error?.message && message.error(error.message);
      savingRef.current = false;
      setSaving(false);
      return;
    }

    // The save went through. Refreshing the profile is best effort: if it fails the sections still
    // leave edit mode and the user is told to reload.
    message.success(t("savedSection", { section: editing.map(sectionTitle).join(", ") }));
    try {
      dispatch(authMe(await AuthApi.me()));
    } catch {
      message.error(t("refreshFailed"));
    }
    setEditing([]);
    savingRef.current = false;
    setSaving(false);
  };

  const handleCancel = () => {
    setEditing([]);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack gap="md" pos="relative">
        <LoadingOverlay visible={saving} />
        {SECTIONS.map((id) => (
          <InfoCard
            key={id}
            id={SECTION_IDS[id]}
            title={sectionTitle(id)}
            sub={editing.includes(id) && id === "address" ? t("addressHelp") : t(`sections.${id}.sub`)}
            editing={editing.includes(id)}
            dirty={isSectionDirty(form.values, baseline, id)}
            saving={saving}
            disabledReason={disabledReason(id)}
            onEdit={() => setEditing([id])}
            onCancel={handleCancel}
          >
            {id === "identity" && <MainInfoIdentitySection form={form} isEdit={editing.includes(id)} />}
            {id === "contact" && <MainInfoContactSection form={form} isEdit={editing.includes(id)} />}
            {id === "address" && <MainInfoAddressSection form={form} isEdit={editing.includes(id)} />}
          </InfoCard>
        ))}
      </Stack>
    </Box>
  );
}
